import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search, RotateCcw, X, Plus, Trash2, Edit2, Eye,
  Users, Truck, Receipt, CheckSquare, Square,
  PackageCheck, AlertCircle, RefreshCw, Info,
  DollarSign, Calendar, FileText, Check, ArrowRight
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';

export default function Refunds() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.pathname.includes('/refunds/supplier') ? 'supplier' : 'customer';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync activeTab whenever URL path changes
  useEffect(() => {
    if (location.pathname.includes('/refunds/supplier')) {
      setActiveTab('supplier');
    } else if (location.pathname.includes('/refunds/customer')) {
      setActiveTab('customer');
    }
  }, [location.pathname]);

  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [customerRefunds, setCustomerRefunds] = useState([]);
  const [supplierRefunds, setSupplierRefunds] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // View Details Modal State
  const [selectedRefundForView, setSelectedRefundForView] = useState(null);

  // Edit Modal State
  const [editingRefund, setEditingRefund] = useState(null);
  const [editForm, setEditForm] = useState({
    refund_date: '',
    reason: '',
    notes: '',
    refund_amount: '',
    items: [],
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Invoices for selected entity
  const [entityInvoices, setEntityInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [loadingInvoiceDetails, setLoadingInvoiceDetails] = useState(false);

  const [manualItems, setManualItems] = useState([
    { medicine_id: '', return_quantity: 1, refund_amount: '' }
  ]);

  const [form, setForm] = useState({
    entity_id: '',
    related_id: '',
    refund_date: new Date().toISOString().slice(0, 10),
    refund_type: 'return',
    refund_amount: '',
    reason: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [custRes, supRes, medRes] = await Promise.all([
        api.get('/customers?limit=500').catch(() => ({ customers: [] })),
        api.get('/suppliers?limit=500').catch(() => ({ suppliers: [] })),
        api.get('/medicines?limit=500').catch(() => ({ medicines: [] })),
      ]);
      setCustomers(Array.isArray(custRes) ? custRes : (custRes?.customers || []));
      setSuppliers(Array.isArray(supRes) ? supRes : (supRes?.suppliers || []));
      setMedicines(Array.isArray(medRes) ? medRes : (medRes?.medicines || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load data');
    }
  };

  const loadCustomerRefunds = async () => {
    try {
      const res = await api.get('/customer-refunds?limit=500');
      setCustomerRefunds(Array.isArray(res) ? res : (res?.refunds || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load customer refunds');
    }
  };

  const loadSupplierRefunds = async () => {
    try {
      const res = await api.get('/supplier-refunds?limit=500');
      setSupplierRefunds(Array.isArray(res) ? res : (res?.refunds || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load supplier refunds');
    }
  };

  const handleSearchMedicines = async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    try {
      const res = await api.get(`/medicines?search=${encodeURIComponent(q)}&limit=100`);
      const foundMeds = Array.isArray(res) ? res : (res?.medicines || []);
      if (foundMeds.length > 0) {
        setMedicines((prev) => {
          const map = new Map(prev.map((m) => [String(m.id), m]));
          for (const m of foundMeds) {
            map.set(String(m.id), m);
          }
          return Array.from(map.values());
        });
      }
    } catch (err) {
      console.error('Failed to search medicines in Refunds', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'customer') loadCustomerRefunds();
    else loadSupplierRefunds();
  }, [activeTab]);

  // Load invoices whenever entity, activeTab, or modal opens
  useEffect(() => {
    if (!showForm) return;

    const loadInvoices = async () => {
      setLoadingInvoices(true);
      try {
        if (activeTab === 'customer') {
          let url = '/sales?limit=200';
          if (form.entity_id === 'walk-in') {
            url = '/sales?customer_id=walk-in&limit=200';
          } else if (form.entity_id) {
            url = `/sales?customer_id=${form.entity_id}&limit=200`;
          }
          const res = await api.get(url);
          const salesList = Array.isArray(res) ? res : (res?.sales || []);
          setEntityInvoices(salesList);
        } else {
          let url = '/purchases?limit=200';
          if (form.entity_id === 'general') {
            url = '/purchases?supplier_id=general&limit=200';
          } else if (form.entity_id) {
            url = `/purchases?supplier_id=${form.entity_id}&limit=200`;
          }
          const res = await api.get(url);
          const purchasesList = Array.isArray(res) ? res : (res?.purchases || []);
          setEntityInvoices(purchasesList);
        }
      } catch (err) {
        console.error('Failed to load invoices:', err);
        setEntityInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };

    loadInvoices();
  }, [form.entity_id, activeTab, showForm]);

  const getMedicineUnits = (medId, fallbackUnit = 'pcs', fallbackPrice = 0) => {
    const med = medicines.find((m) => String(m.id) === String(medId));
    if (med && Array.isArray(med.units) && med.units.length > 0) {
      return med.units.map((u) => ({
        unit_name: u.unit_name,
        conversion_factor: Math.max(1, parseInt(u.conversion_factor) || 1),
        cost_price: parseFloat(u.cost_price) || 0,
        selling_price: parseFloat(u.selling_price) || 0,
        is_base_unit: !!u.is_base_unit,
      }));
    }
    return [
      {
        unit_name: fallbackUnit || med?.unit || 'pcs',
        conversion_factor: 1,
        cost_price: parseFloat(med?.avg_cost_price || fallbackPrice) || 0,
        selling_price: parseFloat(med?.selling_price || fallbackPrice) || 0,
        is_base_unit: true,
      },
    ];
  };

  // Load invoice items whenever related_id (invoice) changes
  useEffect(() => {
    if (!form.related_id) {
      setSelectedInvoiceData(null);
      setInvoiceItems([]);
      return;
    }

    const loadDetails = async () => {
      setLoadingInvoiceDetails(true);
      try {
        if (activeTab === 'customer') {
          const sale = await api.get(`/sales/${form.related_id}`);
          setSelectedInvoiceData(sale);
          const rawItems = sale?.items || [];
          const refundsRes = await api.get(`/customer-refunds?sale_id=${form.related_id}&limit=500`);
          const refundedBaseQty = (refundsRes?.refunds || refundsRes || []).reduce((totals, refund) => {
            if (refund.refund_type !== 'return') return totals;
            (refund.items || []).forEach((item) => {
              totals[item.medicine_id] = (totals[item.medicine_id] || 0) + (parseInt(item.return_quantity) || 0);
            });
            return totals;
          }, {});
          const invoiceSubtotal = parseFloat(sale?.subtotal) || 0;
          const invoiceDiscount = parseFloat(sale?.discount) || 0;
          const invoiceDiscountRatio = (invoiceSubtotal > 0 && invoiceDiscount > 0)
            ? (invoiceDiscount / invoiceSubtotal)
            : 0;

          const items = rawItems.map((it) => {
            const medUnits = getMedicineUnits(it.medicine_id, it.unit_name || it.medicine_unit || 'pcs', it.unit_price);
            const invoicedUnitName = it.unit_name || it.medicine_unit || (medUnits[0]?.unit_name || 'pcs');
            const matchedUnit = medUnits.find((u) => u.unit_name === invoicedUnitName) || medUnits[0];
            const conv = Math.max(1, parseInt(it.conversion_factor || matchedUnit?.conversion_factor) || 1);
            const originalQty = parseInt(it.unit_quantity || it.quantity) || 1;
            const originalBaseQty = parseInt(it.quantity) || (originalQty * conv);
            const remainingBaseQty = Math.max(0, originalBaseQty - (refundedBaseQty[it.medicine_id] || 0));
            const maxReturnQty = Math.floor(remainingBaseQty / conv);
            if (maxReturnQty <= 0) return null;

            // Gross unit price before any discount
            const grossUnitPrice = parseFloat(it.unit_price) || (matchedUnit?.selling_price || 0);
            const lineGrossTotal = grossUnitPrice * originalQty;

            // Line item discount from sale item row
            const itemLineDiscount = parseFloat(it.discount) || 0;
            const lineTotalAfterItemDisc = parseFloat(it.total) || Math.max(0, lineGrossTotal - itemLineDiscount);

            // Proportional share of invoice-level discount
            const allocatedInvoiceDiscount = invoiceDiscountRatio > 0
              ? (lineTotalAfterItemDisc * invoiceDiscountRatio)
              : 0;

            // Net total and effective unit price
            const itemNetTotal = Math.max(0, lineTotalAfterItemDisc - allocatedInvoiceDiscount);
            const netUnitPrice = originalQty > 0 ? (itemNetTotal / originalQty) : grossUnitPrice;
            const discountPercent = lineGrossTotal > 0
              ? Math.max(0, ((lineGrossTotal - itemNetTotal) / lineGrossTotal) * 100)
              : 0;

            return {
              medicine_id: it.medicine_id,
              medicine_name: it.medicine_name || `Medicine #${it.medicine_id}`,
              available_units: medUnits,
              selected_unit: matchedUnit?.unit_name || invoicedUnitName,
              conversion_factor: conv,
              invoiced_qty: originalQty,
              invoiced_base_qty: originalBaseQty,
              invoiced_unit_name: invoicedUnitName,
              gross_unit_price: grossUnitPrice,
              unit_price: netUnitPrice,
              discount_percent: discountPercent,
              return_qty: maxReturnQty,
              max_return_qty: maxReturnQty,
              remaining_base_qty: remainingBaseQty,
              refund_amount: (maxReturnQty * netUnitPrice).toFixed(2),
              batch_number: it.batch_number || '-',
              selected: true,
            };
          }).filter(Boolean);
          setInvoiceItems(items);
        } else {
          const purchase = await api.get(`/purchases/${form.related_id}`);
          setSelectedInvoiceData(purchase);
          const rawItems = purchase?.items || [];
          const refundsRes = await api.get(`/supplier-refunds?purchase_id=${form.related_id}&limit=500`);
          const refundedBaseQty = (refundsRes?.refunds || refundsRes || []).reduce((totals, refund) => {
            if (refund.refund_type !== 'return') return totals;
            (refund.items || []).forEach((item) => {
              totals[item.medicine_id] = (totals[item.medicine_id] || 0) + (parseInt(item.return_quantity) || 0);
            });
            return totals;
          }, {});
          const items = rawItems.map((it) => {
            const medUnits = getMedicineUnits(it.medicine_id, it.unit_name || it.unit || it.medicine_unit || 'pcs', it.buy_price || it.unit_price);
            const invoicedUnitName = it.unit_name || it.unit || it.medicine_unit || (medUnits[0]?.unit_name || 'pcs');
            const matchedUnit = medUnits.find((u) => u.unit_name === invoicedUnitName) || medUnits[0];
            const conv = Math.max(1, parseInt(it.conversion_factor || matchedUnit?.conversion_factor) || 1);
            const originalQty = parseInt(it.unit_quantity || it.quantity) || 1;
            const originalBaseQty = parseInt(it.quantity) || (originalQty * conv);
            const remainingBaseQty = Math.max(0, originalBaseQty - (refundedBaseQty[it.medicine_id] || 0));
            const maxReturnQty = Math.floor(remainingBaseQty / conv);
            if (maxReturnQty <= 0) return null;

            // Gross buy price before discount
            const grossBuyPrice = parseFloat(it.buy_price || it.unit_price) || (matchedUnit?.cost_price || 0);
            const lineGrossTotal = grossBuyPrice * originalQty;

            // Purchase item totals already include their line discount. Applying
            // purchase.discount again here would deduct the same discount twice.
            const itemLineTotal = parseFloat(it.total) || lineGrossTotal;
            const itemNetTotal = Math.max(0, itemLineTotal);
            const netBuyPrice = originalQty > 0 ? (itemNetTotal / originalQty) : grossBuyPrice;
            const discountPercent = lineGrossTotal > 0
              ? Math.max(0, ((lineGrossTotal - itemNetTotal) / lineGrossTotal) * 100)
              : 0;

            return {
              medicine_id: it.medicine_id,
              medicine_name: it.medicine_name || `Medicine #${it.medicine_id}`,
              available_units: medUnits,
              selected_unit: matchedUnit?.unit_name || invoicedUnitName,
              conversion_factor: conv,
              invoiced_qty: originalQty,
              invoiced_base_qty: originalBaseQty,
              invoiced_unit_name: invoicedUnitName,
              gross_unit_price: grossBuyPrice,
              unit_price: netBuyPrice,
              discount_percent: discountPercent,
              return_qty: maxReturnQty,
              max_return_qty: maxReturnQty,
              remaining_base_qty: remainingBaseQty,
              refund_amount: (maxReturnQty * netBuyPrice).toFixed(2),
              batch_number: it.batch_number || '-',
              selected: true,
            };
          }).filter(Boolean);
          setInvoiceItems(items);
        }
      } catch (err) {
        console.error('Failed to load invoice items:', err);
        setSelectedInvoiceData(null);
        setInvoiceItems([]);
      } finally {
        setLoadingInvoiceDetails(false);
      }
    };

    loadDetails();
  }, [form.related_id, activeTab]);

  const openForm = () => {
    setForm({
      entity_id: activeTab === 'customer' ? 'walk-in' : 'general',
      related_id: '',
      refund_date: new Date().toISOString().slice(0, 10),
      refund_type: 'return',
      refund_amount: '',
      reason: '',
      notes: '',
    });
    setManualItems([{ medicine_id: '', unit_name: '', conversion_factor: 1, unit_price: 0, return_quantity: 1, refund_amount: '' }]);
    setEntityInvoices([]);
    setSelectedInvoiceData(null);
    setInvoiceItems([]);
    setShowForm(true);
  };

  const handleInvoiceChange = (val) => {
    if (!val) {
      setForm((prev) => ({ ...prev, related_id: '' }));
      return;
    }
    const selectedInv = entityInvoices.find((inv) => String(inv.id) === String(val));
    if (selectedInv) {
      let autoEntityId = form.entity_id;
      if (activeTab === 'customer') {
        autoEntityId = selectedInv.customer_id ? String(selectedInv.customer_id) : 'walk-in';
      } else {
        autoEntityId = selectedInv.supplier_id ? String(selectedInv.supplier_id) : 'general';
      }
      setForm((prev) => ({ ...prev, related_id: val, entity_id: autoEntityId }));
    } else {
      setForm((prev) => ({ ...prev, related_id: val }));
    }
  };

  const handleToggleItem = (index) => {
    setInvoiceItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: !updated[index].selected };
      return updated;
    });
  };

  const handleItemQtyChange = (index, qtyVal) => {
    setInvoiceItems((prev) => {
      const updated = [...prev];
      const item = updated[index];
      let newQty = parseInt(qtyVal, 10);
      if (isNaN(newQty) || newQty < 1) newQty = 1;
      if (item.max_return_qty && newQty > item.max_return_qty) {
        toast.error(`Only ${item.max_return_qty} unit(s) remain refundable`);
        newQty = item.max_return_qty;
      }
      const refAmt = (newQty * item.unit_price).toFixed(2);
      updated[index] = { ...item, return_qty: newQty, refund_amount: refAmt };
      return updated;
    });
  };

  const handleItemUnitChange = (index, unitName) => {
    setInvoiceItems((prev) => {
      const updated = [...prev];
      const item = updated[index];
      const newUnit = item.available_units.find((u) => u.unit_name === unitName) || item.available_units[0];
      const grossPrice = activeTab === 'customer'
        ? (parseFloat(newUnit?.selling_price) || 0)
        : (parseFloat(newUnit?.cost_price) || 0);

      const discPct = item.discount_percent || 0;
      const netPrice = discPct > 0 ? (grossPrice * (1 - discPct / 100)) : grossPrice;
      const conv = Math.max(1, parseInt(newUnit?.conversion_factor) || 1);
      const maxReturnQty = Math.floor((item.remaining_base_qty || 0) / conv);
      if (maxReturnQty < 1) {
        toast.error('The remaining quantity is smaller than the selected unit');
        return prev;
      }
      const returnQty = Math.min(item.return_qty || 1, Math.max(1, maxReturnQty));
      const refAmt = (returnQty * netPrice).toFixed(2);

      updated[index] = {
        ...item,
        selected_unit: newUnit?.unit_name || unitName,
        conversion_factor: conv,
        gross_unit_price: grossPrice,
        unit_price: netPrice,
        return_qty: returnQty,
        max_return_qty: maxReturnQty,
        refund_amount: refAmt,
      };
      return updated;
    });
  };

  const handleSelectAllItems = (selectAll) => {
    setInvoiceItems((prev) => prev.map((it) => ({ ...it, selected: selectAll })));
  };

  const calculateTotalInvoiceRefund = () => {
    return invoiceItems
      .filter((it) => it.selected)
      .reduce((sum, it) => sum + (parseFloat(it.refund_amount) || 0), 0);
  };

  const handleAddManualItem = () => {
    setManualItems((prev) => [
      ...prev,
      { medicine_id: '', unit_name: '', conversion_factor: 1, unit_price: 0, return_quantity: 1, refund_amount: '' },
    ]);
  };

  const handleRemoveManualItem = (index) => {
    setManualItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleManualItemChange = (index, field, value) => {
    setManualItems((prev) => {
      const updated = [...prev];
      let item = { ...updated[index], [field]: value };
      const med = medicines.find((m) => String(m.id) === String(item.medicine_id));
      const medUnits = med ? getMedicineUnits(med.id) : [];

      if (field === 'medicine_id') {
        const selectedMed = medicines.find((m) => String(m.id) === String(value));
        const units = selectedMed ? getMedicineUnits(selectedMed.id) : [];
        const defaultUnit = units[0] || { unit_name: selectedMed?.unit || 'pcs', conversion_factor: 1, selling_price: selectedMed?.selling_price || 0, cost_price: selectedMed?.avg_cost_price || 0 };
        const price = activeTab === 'customer' ? (parseFloat(defaultUnit.selling_price) || 0) : (parseFloat(defaultUnit.cost_price) || 0);
        const qty = parseInt(item.return_quantity) || 1;
        item.medicine_id = value;
        item.unit_name = defaultUnit.unit_name;
        item.conversion_factor = defaultUnit.conversion_factor;
        item.unit_price = price;
        item.refund_amount = (qty * price).toFixed(2);
      } else if (field === 'unit_name') {
        const unitObj = medUnits.find((u) => u.unit_name === value) || medUnits[0];
        if (unitObj) {
          const price = activeTab === 'customer' ? (parseFloat(unitObj.selling_price) || 0) : (parseFloat(unitObj.cost_price) || 0);
          const qty = parseInt(item.return_quantity) || 1;
          item.unit_name = unitObj.unit_name;
          item.conversion_factor = unitObj.conversion_factor;
          item.unit_price = price;
          item.refund_amount = (qty * price).toFixed(2);
        }
      } else if (field === 'return_quantity') {
        const qty = parseInt(value) || 1;
        const price = parseFloat(item.unit_price) || 0;
        item.refund_amount = (qty * price).toFixed(2);
      } else if (field === 'refund_amount') {
        item.refund_amount = value;
      }

      updated[index] = item;
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCustomer = activeTab === 'customer';

    if (!form.entity_id && !form.related_id) {
      toast.error(isCustomer ? 'Customer or invoice is required' : 'Supplier is required');
      return;
    }

    const customerIdValue = (form.entity_id === 'walk-in' || !form.entity_id) ? null : parseInt(form.entity_id);
    const supplierIdValue = (form.entity_id === 'general' || !form.entity_id) ? null : parseInt(form.entity_id);

    setSubmitting(true);
    try {
      if (form.refund_type === 'return') {
        if (form.related_id && invoiceItems.length > 0) {
          const selectedItems = invoiceItems.filter((it) => it.selected);
          if (selectedItems.length === 0) {
            toast.error('Please select at least one item from the invoice to return');
            setSubmitting(false);
            return;
          }

          const parsedItemsPayload = selectedItems.map((item) => {
            const unitQty = parseInt(item.return_qty) || 1;
            const conv = parseInt(item.conversion_factor) || 1;
            return {
              medicine_id: parseInt(item.medicine_id),
              unit_name: item.selected_unit || null,
              unit_quantity: unitQty,
              conversion_factor: conv,
              return_quantity: unitQty * conv,
              unit_price: parseFloat(item.unit_price) || 0,
              refund_amount: parseFloat(item.refund_amount) || 0,
            };
          });

          const totalAmount = parsedItemsPayload.reduce((sum, it) => sum + it.refund_amount, 0);

          const payload = {
            refund_date: form.refund_date,
            refund_type: 'return',
            refund_amount: totalAmount,
            reason: form.reason,
            notes: form.notes,
            user_id: user?.id ? parseInt(user.id) : 1,
            items: parsedItemsPayload,
            ...(isCustomer
              ? { customer_id: customerIdValue, sale_id: parseInt(form.related_id) }
              : { supplier_id: supplierIdValue, purchase_id: parseInt(form.related_id) }),
          };

          await api.post(isCustomer ? '/customer-refunds' : '/supplier-refunds', payload);
          toast.success(`${selectedItems.length} item(s) refunded in single consolidated return`);
        } else {
          // Manual return without invoice
          const validManualItems = manualItems.filter((it) => it.medicine_id && parseInt(it.return_quantity) > 0);
          if (validManualItems.length === 0) {
            toast.error('Please select at least one medicine and return quantity');
            setSubmitting(false);
            return;
          }

          const parsedItemsPayload = validManualItems.map((item) => {
            const unitQty = parseInt(item.return_quantity) || 1;
            const conv = parseInt(item.conversion_factor) || 1;
            const price = parseFloat(item.unit_price) || 0;
            const refAmount = parseFloat(item.refund_amount) || (unitQty * price);
            return {
              medicine_id: parseInt(item.medicine_id),
              unit_name: item.unit_name || null,
              unit_quantity: unitQty,
              conversion_factor: conv,
              return_quantity: unitQty * conv,
              unit_price: price,
              refund_amount: refAmount,
            };
          });

          const totalAmount = parsedItemsPayload.reduce((sum, it) => sum + it.refund_amount, 0);

          const payload = {
            refund_date: form.refund_date,
            refund_type: 'return',
            refund_amount: totalAmount,
            reason: form.reason,
            notes: form.notes,
            user_id: user?.id ? parseInt(user.id) : 1,
            items: parsedItemsPayload,
            ...(isCustomer
              ? { customer_id: customerIdValue, sale_id: form.related_id ? parseInt(form.related_id) : null }
              : { supplier_id: supplierIdValue, purchase_id: form.related_id ? parseInt(form.related_id) : null }),
          };

          await api.post(isCustomer ? '/customer-refunds' : '/supplier-refunds', payload);
          toast.success(`${validManualItems.length} medicine(s) refunded successfully`);
        }
      } else {
        // Overpayment refund
        if (!form.refund_amount || parseFloat(form.refund_amount) <= 0) {
          toast.error('Refund amount is required');
          setSubmitting(false);
          return;
        }

        const payload = {
          refund_date: form.refund_date,
          refund_type: 'overpayment',
          refund_amount: parseFloat(form.refund_amount),
          reason: form.reason,
          notes: form.notes,
          user_id: user?.id ? parseInt(user.id) : 1,
          items: [],
          ...(isCustomer
            ? { customer_id: customerIdValue, sale_id: form.related_id ? parseInt(form.related_id) : null }
            : { supplier_id: supplierIdValue, purchase_id: form.related_id ? parseInt(form.related_id) : null }),
        };

        await api.post(isCustomer ? '/customer-refunds' : '/supplier-refunds', payload);
        toast.success(isCustomer ? 'Customer overpayment refund processed' : 'Supplier overpayment refund processed');
      }

      setShowForm(false);
      if (isCustomer) loadCustomerRefunds();
      else loadSupplierRefunds();
    } catch (err) {
      toast.error(err.message || 'Failed to process refund');
    } finally {
      setSubmitting(false);
    }
  };

  // View Details Modal Handler
  const handleViewDetails = async (refund) => {
    setSelectedRefundForView(refund);
    try {
      const url = activeTab === 'customer' ? `/customer-refunds/${refund.id}` : `/supplier-refunds/${refund.id}`;
      const res = await api.get(url);
      if (res?.refund) {
        setSelectedRefundForView(res.refund);
      }
    } catch (err) {
      console.error('Failed to fetch full refund details:', err);
    }
  };

  // Edit Modal Handler
  const handleOpenEdit = async (refund) => {
    try {
      let fullRefund = refund;
      const url = activeTab === 'customer' ? `/customer-refunds/${refund.id}` : `/supplier-refunds/${refund.id}`;
      const res = await api.get(url);
      if (res?.refund) {
        fullRefund = res.refund;
      }

      setEditingRefund(fullRefund);
      setEditForm({
        refund_date: fullRefund.refund_date ? fullRefund.refund_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
        reason: fullRefund.reason || '',
        notes: fullRefund.notes || '',
        refund_amount: fullRefund.refund_amount || '',
        items: (fullRefund.items || []).map((it) => ({
          ...it,
          unit_quantity: it.unit_quantity || it.return_quantity || 1,
          unit_price: parseFloat(it.unit_price) || (it.refund_amount && it.unit_quantity ? (it.refund_amount / it.unit_quantity) : 0),
          refund_amount: parseFloat(it.refund_amount) || 0,
        })),
      });
    } catch (err) {
      toast.error('Failed to prepare refund edit');
    }
  };

  const handleEditItemQtyChange = (idx, newQtyVal) => {
    setEditForm((prev) => {
      const updatedItems = [...prev.items];
      const item = updatedItems[idx];
      let newQty = parseInt(newQtyVal, 10);
      if (isNaN(newQty) || newQty < 1) newQty = 1;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const lineTotal = (newQty * unitPrice).toFixed(2);
      updatedItems[idx] = { ...item, unit_quantity: newQty, refund_amount: lineTotal };

      const newTotal = updatedItems.reduce((sum, it) => sum + (parseFloat(it.refund_amount) || 0), 0);
      return {
        ...prev,
        items: updatedItems,
        refund_amount: newTotal.toFixed(2),
      };
    });
  };

  const handleEditItemAmountChange = (idx, newAmtVal) => {
    setEditForm((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[idx] = { ...updatedItems[idx], refund_amount: newAmtVal };
      const newTotal = updatedItems.reduce((sum, it) => sum + (parseFloat(it.refund_amount) || 0), 0);
      return {
        ...prev,
        items: updatedItems,
        refund_amount: newTotal.toFixed(2),
      };
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRefund) return;

    setSavingEdit(true);
    const isCustomer = activeTab === 'customer';
    try {
      const url = isCustomer ? `/customer-refunds/${editingRefund.id}` : `/supplier-refunds/${editingRefund.id}`;
      const payload = {
        refund_date: editForm.refund_date,
        reason: editForm.reason,
        notes: editForm.notes,
        refund_amount: parseFloat(editForm.refund_amount) || 0,
        items: editForm.items.map((it) => ({
          medicine_id: it.medicine_id,
          unit_name: it.unit_name || null,
          unit_quantity: parseInt(it.unit_quantity) || 1,
          conversion_factor: parseInt(it.conversion_factor) || 1,
          return_quantity: (parseInt(it.unit_quantity) || 1) * (parseInt(it.conversion_factor) || 1),
          unit_price: parseFloat(it.unit_price) || 0,
          refund_amount: parseFloat(it.refund_amount) || 0,
        })),
      };

      await api.put(url, payload);
      toast.success('Refund updated successfully');
      setEditingRefund(null);
      if (selectedRefundForView && selectedRefundForView.id === editingRefund.id) {
        setSelectedRefundForView(null);
      }
      if (isCustomer) loadCustomerRefunds();
      else loadSupplierRefunds();
    } catch (err) {
      toast.error(err.message || 'Failed to update refund');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!confirm('Are you sure you want to delete this refund? This will restore customer balance and reverse inventory additions.')) return;
    try {
      if (type === 'customer') {
        await api.delete(`/customer-refunds/${id}`);
        toast.success('Customer refund deleted');
        if (selectedRefundForView && selectedRefundForView.id === id) setSelectedRefundForView(null);
        loadCustomerRefunds();
      } else {
        await api.delete(`/supplier-refunds/${id}`);
        toast.success('Supplier refund deleted');
        if (selectedRefundForView && selectedRefundForView.id === id) setSelectedRefundForView(null);
        loadSupplierRefunds();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete refund');
    }
  };

  const filterByDateAndSearch = (items, entityNameField, invoiceField) => {
    return items.filter((r) => {
      const q = search.toLowerCase();
      const matchSearch = !search ||
        (r[entityNameField] || '').toLowerCase().includes(q) ||
        (r[invoiceField] || '').toLowerCase().includes(q) ||
        (r.items_summary || '').toLowerCase().includes(q) ||
        (r.legacy_medicine_name || '').toLowerCase().includes(q) ||
        (r.reason || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q);

      let matchDate = true;
      if (dateFilter.from) matchDate = matchDate && r.refund_date >= dateFilter.from;
      if (dateFilter.to) matchDate = matchDate && r.refund_date <= dateFilter.to;
      return matchSearch && matchDate;
    });
  };

  const filteredCustomerRefunds = filterByDateAndSearch(customerRefunds, 'customer_name', 'sale_invoice_number');
  const filteredSupplierRefunds = filterByDateAndSearch(supplierRefunds, 'supplier_name', 'purchase_reference');

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const customerOptions = [
    { value: 'walk-in', label: 'Walk-in Customer', sublabel: 'Direct / unregistered counter sales' },
    ...customers.map((c) => ({
      value: String(c.id),
      label: c.name,
      sublabel: c.phone ? `Phone: ${c.phone}` : null,
    })),
  ];

  const supplierOptions = [
    { value: 'general', label: 'General Supplier', sublabel: 'Direct / unregistered purchases' },
    ...suppliers.map((s) => ({
      value: String(s.id),
      label: s.name,
      sublabel: s.phone ? `Phone: ${s.phone}` : null,
    })),
  ];

  const medicineOptions = medicines.map((m) => {
    const subParts = [
      m.generic_name ? `Generic: ${m.generic_name}` : null,
      m.selling_price ? `Price: Rs. ${parseFloat(m.selling_price).toFixed(2)}` : null,
      m.barcode ? `Barcode: ${m.barcode}` : null,
      m.sku ? `SKU: ${m.sku}` : null,
    ].filter(Boolean).join(' • ');

    return {
      value: String(m.id),
      label: m.name,
      sublabel: subParts || null,
      keywords: `${m.name} ${m.generic_name || ''} ${m.barcode || ''} ${m.sku || ''}`,
    };
  });

  const availableEntityInvoices = entityInvoices.filter((inv) => {
    const relatedId = activeTab === 'customer' ? inv.id : inv.id;
    const refunds = activeTab === 'customer' ? customerRefunds : supplierRefunds;
    const refundedAmount = refunds
      .filter((refund) => refund.refund_type === 'return' && String(activeTab === 'customer' ? refund.sale_id : refund.purchase_id) === String(relatedId))
      .reduce((sum, refund) => sum + (parseFloat(refund.refund_amount) || 0), 0);
    return refundedAmount + 0.005 < (parseFloat(inv.total) || 0);
  });

  const invoiceOptions = [
    { value: '', label: '-- Direct / No Invoice (Manual Return) --', sublabel: null },
    ...availableEntityInvoices.map((inv) => {
      const isWalkIn = !inv.customer_id || inv.customer_id === 0;
      const contactLabel = activeTab === 'customer'
        ? (inv.customer_name || (isWalkIn ? 'Walk-in Customer' : `Customer #${inv.customer_id}`))
        : (inv.supplier_name || (inv.supplier_id ? `Supplier #${inv.supplier_id}` : 'General Supplier'));
      return {
        value: String(inv.id),
        label: activeTab === 'customer'
          ? `${inv.invoice_number || `INV-${inv.id}`} — ${contactLabel} — Rs. ${(parseFloat(inv.total) || 0).toFixed(2)}`
          : `${inv.reference_number || `PUR-${inv.id}`} — ${contactLabel} — Rs. ${(parseFloat(inv.total) || 0).toFixed(2)}`,
        sublabel: `Date: ${fmtDate(inv.created_at || inv.purchase_date)} | Status: ${inv.payment_status || 'paid'}`,
      };
    }),
  ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-white p-1 w-fit shadow-sm">
        {[
          { key: 'customer', label: 'Customer Refunds', icon: Users, color: 'text-emerald-600' },
          { key: 'supplier', label: 'Supplier Refunds', icon: Truck, color: 'text-amber-600' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setSearch('');
              setDateFilter({ from: '', to: '' });
              navigate(`/refunds/${tab.key}`);
            }}
            className={clsx(
              'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
              activeTab === tab.key
                ? activeTab === 'customer'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-amber-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            )}
          >
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Customer Tab */}
      {activeTab === 'customer' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Customer Refunds</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                Rs. {filteredCustomerRefunds.reduce((s, r) => s + (parseFloat(r.refund_amount) || 0), 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400 mt-1">{filteredCustomerRefunds.length} transactions recorded</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Goods Returns</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {filteredCustomerRefunds.filter((r) => r.refund_type === 'return').length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Return transactions to inventory</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Overpayment Refunds</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {filteredCustomerRefunds.filter((r) => r.refund_type === 'overpayment').length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Cash / balance adjustments</p>
            </div>
          </div>

          <DateFilter
            dateFrom={dateFilter.from}
            dateTo={dateFilter.to}
            onDateFromChange={(val) => setDateFilter({ ...dateFilter, from: val })}
            onDateToChange={(val) => setDateFilter({ ...dateFilter, to: val })}
            onApply={() => {}}
            onReset={() => setDateFilter({ from: '', to: '' })}
          />

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer, invoice #, medicine, or reason..."
                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
            <button
              onClick={openForm}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.97] transition-all"
            >
              <Plus className="h-4 w-4" /> New Customer Refund
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Customer</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Invoice #</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Returned Items & Qty</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Total Refund</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reason</th>
                    <th className="sticky-th-right px-6 py-3.5 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCustomerRefunds.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                        No customer refunds found
                      </td>
                    </tr>
                  )}
                  {filteredCustomerRefunds.map((r) => {
                    const items = r.items || [];
                    const hasMultipleItems = items.length > 1;

                    return (
                      <tr key={r.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {fmtDate(r.refund_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                          {r.customer_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                          {r.sale_invoice_number ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                              {r.sale_invoice_number}
                            </span>
                          ) : r.sale_id ? (
                            `INV-${r.sale_id}`
                          ) : (
                            <span className="text-gray-400">Direct / Manual</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-sm">
                          {r.refund_type === 'return' ? (
                            hasMultipleItems ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                    <PackageCheck className="h-3.5 w-3.5" />
                                    {items.length} Items Returned
                                  </span>
                                  <button
                                    onClick={() => handleViewDetails(r)}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium hover:underline flex items-center gap-0.5"
                                  >
                                    View breakdown
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 truncate" title={r.items_summary || items.map(it => it.medicine_name).join(', ')}>
                                  {items.map((it) => it.medicine_name).join(', ')}
                                </p>
                              </div>
                            ) : items.length === 1 ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-900">{items[0].medicine_name}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  {items[0].unit_name
                                    ? `${items[0].unit_quantity} ${items[0].unit_name}`
                                    : `${items[0].return_quantity} ${items[0].medicine_unit || 'pcs'}`}
                                </span>
                              </div>
                            ) : r.legacy_medicine_name ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-900">{r.legacy_medicine_name}</span>
                                {r.return_quantity && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                    {r.unit_name ? `${r.unit_quantity || r.return_quantity} ${r.unit_name}` : `${r.return_quantity} ${r.legacy_medicine_unit || 'units'}`}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-purple-700 font-medium bg-purple-50 px-2 py-0.5 rounded border border-purple-100">
                              <DollarSign className="h-3 w-3" /> Overpayment / Balance Refund
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                            r.refund_type === 'return' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>
                            {r.refund_type === 'return' ? 'Goods Return' : 'Overpayment'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                          Rs. {(parseFloat(r.refund_amount) || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                          {r.reason || r.notes || '-'}
                        </td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleViewDetails(r)}
                              className="rounded-lg p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                              title="View Breakdown & Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleOpenEdit(r)}
                              className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                              title="Edit Refund"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(r.id, 'customer')}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Supplier Tab */}
      {activeTab === 'supplier' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Supplier Refunds</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                Rs. {filteredSupplierRefunds.reduce((s, r) => s + (parseFloat(r.refund_amount) || 0), 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400 mt-1">{filteredSupplierRefunds.length} transactions recorded</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Goods Returns</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {filteredSupplierRefunds.filter((r) => r.refund_type === 'return').length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Returned to supplier</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Overpayment Refunds</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {filteredSupplierRefunds.filter((r) => r.refund_type === 'overpayment').length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Credit / cash recoveries</p>
            </div>
          </div>

          <DateFilter
            dateFrom={dateFilter.from}
            dateTo={dateFilter.to}
            onDateFromChange={(val) => setDateFilter({ ...dateFilter, from: val })}
            onDateToChange={(val) => setDateFilter({ ...dateFilter, to: val })}
            onApply={() => {}}
            onReset={() => setDateFilter({ from: '', to: '' })}
          />

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by supplier, purchase ref, medicine, or reason..."
                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
            <button
              onClick={openForm}
              className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 active:scale-[0.97] transition-all"
            >
              <Plus className="h-4 w-4" /> New Supplier Refund
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="sticky-th-left px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Supplier</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Purchase Ref</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Returned Items & Qty</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Total Refund</th>
                    <th className="px-6 py-3.5 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reason</th>
                    <th className="sticky-th-right px-6 py-3.5 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredSupplierRefunds.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                        No supplier refunds found
                      </td>
                    </tr>
                  )}
                  {filteredSupplierRefunds.map((r) => {
                    const items = r.items || [];
                    const hasMultipleItems = items.length > 1;

                    return (
                      <tr key={r.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {fmtDate(r.refund_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                          {r.supplier_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                          {r.purchase_reference ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                              {r.purchase_reference}
                            </span>
                          ) : r.purchase_id ? (
                            `PUR-${r.purchase_id}`
                          ) : (
                            <span className="text-gray-400">Direct / Manual</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-sm">
                          {r.refund_type === 'return' ? (
                            hasMultipleItems ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                                    <PackageCheck className="h-3.5 w-3.5" />
                                    {items.length} Items Returned
                                  </span>
                                  <button
                                    onClick={() => handleViewDetails(r)}
                                    className="text-xs text-amber-700 hover:text-amber-800 font-medium hover:underline flex items-center gap-0.5"
                                  >
                                    View breakdown
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 truncate" title={r.items_summary || items.map(it => it.medicine_name).join(', ')}>
                                  {items.map((it) => it.medicine_name).join(', ')}
                                </p>
                              </div>
                            ) : items.length === 1 ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-900">{items[0].medicine_name}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                  {items[0].unit_name
                                    ? `${items[0].unit_quantity} ${items[0].unit_name}`
                                    : `${items[0].return_quantity} ${items[0].medicine_unit || 'pcs'}`}
                                </span>
                              </div>
                            ) : r.legacy_medicine_name ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-900">{r.legacy_medicine_name}</span>
                                {r.return_quantity && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                    {r.unit_name ? `${r.unit_quantity || r.return_quantity} ${r.unit_name}` : `${r.return_quantity} ${r.legacy_medicine_unit || 'units'}`}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-purple-700 font-medium bg-purple-50 px-2 py-0.5 rounded border border-purple-100">
                              <DollarSign className="h-3 w-3" /> Overpayment / Balance Refund
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                            r.refund_type === 'return' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>
                            {r.refund_type === 'return' ? 'Goods Return' : 'Overpayment'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                          Rs. {(parseFloat(r.refund_amount) || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                          {r.reason || r.notes || '-'}
                        </td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleViewDetails(r)}
                              className="rounded-lg p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                              title="View Breakdown & Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleOpenEdit(r)}
                              className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                              title="Edit Refund"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(r.id, 'supplier')}
                              className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* View Details Modal */}
      {selectedRefundForView && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setSelectedRefundForView(null)} />
          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className={clsx(
                  'flex h-10 w-10 items-center justify-center rounded-xl',
                  activeTab === 'customer' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                )}>
                  <Receipt className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-gray-900">
                      {activeTab === 'customer' ? 'Customer Refund Details' : 'Supplier Refund Details'}
                    </h3>
                    <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                      selectedRefundForView.refund_type === 'return' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700')}>
                      {selectedRefundForView.refund_type === 'return' ? 'Goods Return' : 'Overpayment'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Transaction ID: #{selectedRefundForView.id} | Date: {fmtDate(selectedRefundForView.refund_date)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRefundForView(null)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    {activeTab === 'customer' ? 'Customer Info' : 'Supplier Info'}
                  </p>
                  <p className="text-sm font-bold text-gray-900 mt-1">
                    {activeTab === 'customer'
                      ? (selectedRefundForView.customer_name || 'Walk-in Customer')
                      : (selectedRefundForView.supplier_name || 'General Supplier')}
                  </p>
                  {selectedRefundForView.customer_phone && (
                    <p className="text-xs text-gray-500 mt-0.5">{selectedRefundForView.customer_phone}</p>
                  )}
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Invoice Reference</p>
                  <p className="text-sm font-bold text-gray-900 font-mono mt-1">
                    {activeTab === 'customer'
                      ? (selectedRefundForView.sale_invoice_number || (selectedRefundForView.sale_id ? `INV-${selectedRefundForView.sale_id}` : 'Direct / Manual'))
                      : (selectedRefundForView.purchase_reference || (selectedRefundForView.purchase_id ? `PUR-${selectedRefundForView.purchase_id}` : 'Direct / Manual'))}
                  </p>
                  {selectedRefundForView.sale_total && (
                    <p className="text-xs text-gray-500 mt-0.5">Inv Total: Rs. {parseFloat(selectedRefundForView.sale_total).toFixed(2)}</p>
                  )}
                </div>

                <div className="rounded-xl border border-red-200 bg-red-50/40 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">Total Refunded</p>
                  <p className="text-lg font-bold text-red-600 mt-1">
                    Rs. {(parseFloat(selectedRefundForView.refund_amount) || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Restored to Balance / Stock
                  </p>
                </div>
              </div>

              {/* Items Breakdown Table */}
              {selectedRefundForView.refund_type === 'return' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                      <PackageCheck className="h-4 w-4 text-emerald-600" />
                      Returned Items Breakdown ({(selectedRefundForView.items || []).length})
                    </h4>
                  </div>

                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                          <th className="px-4 py-2.5">#</th>
                          <th className="px-4 py-2.5">Medicine</th>
                          <th className="px-4 py-2.5">Unit Returned</th>
                          <th className="px-4 py-2.5">Base Qty</th>
                          <th className="px-4 py-2.5 text-right">Unit Price</th>
                          <th className="px-4 py-2.5 text-right">Refund Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-sm">
                        {(selectedRefundForView.items || []).length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-xs">
                              No item details recorded for this return.
                            </td>
                          </tr>
                        ) : (
                          (selectedRefundForView.items || []).map((it, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="px-4 py-3 text-xs text-gray-400 font-mono">{idx + 1}</td>
                              <td className="px-4 py-3 font-medium text-gray-900">
                                {it.medicine_name}
                                {it.medicine_generic_name && (
                                  <span className="block text-[11px] text-gray-400">{it.medicine_generic_name}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                  {it.unit_quantity || it.return_quantity} {it.unit_name || it.medicine_unit || 'pcs'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                                {it.return_quantity} {it.medicine_unit || 'pcs'}
                                {it.conversion_factor > 1 && (
                                  <span className="text-[10px] text-gray-400 ml-1">({it.conversion_factor}x)</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-700">
                                Rs. {(parseFloat(it.unit_price) || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-red-600">
                                Rs. {(parseFloat(it.refund_amount) || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-200 bg-gray-50/60 font-bold text-sm">
                          <td colSpan={5} className="px-4 py-3 text-right text-gray-700">
                            Total Refund Amount:
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            Rs. {(parseFloat(selectedRefundForView.refund_amount) || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Reason & Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Reason</p>
                  <p className="text-xs text-gray-800 mt-1">
                    {selectedRefundForView.reason || <span className="text-gray-400">None specified</span>}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-3.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notes & Audit</p>
                  <p className="text-xs text-gray-800 mt-1">
                    {selectedRefundForView.notes || <span className="text-gray-400">No additional notes</span>}
                  </p>
                  {selectedRefundForView.user_name && (
                    <p className="text-[11px] text-gray-400 mt-2">
                      Processed by: <span className="font-semibold text-gray-600">{selectedRefundForView.user_name}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-gray-50/50">
              <button
                onClick={() => {
                  const toEdit = selectedRefundForView;
                  setSelectedRefundForView(null);
                  handleOpenEdit(toEdit);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <Edit2 className="h-3.5 w-3.5" /> Edit This Refund
              </button>

              <button
                onClick={() => setSelectedRefundForView(null)}
                className="rounded-lg bg-gray-800 px-5 py-2 text-xs font-semibold text-white hover:bg-gray-900 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Refund Modal */}
      {editingRefund && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => !savingEdit && setEditingRefund(null)} />
          <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 bg-amber-50/40">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <Edit2 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Edit {activeTab === 'customer' ? 'Customer' : 'Supplier'} Refund #{editingRefund.id}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Update return details, quantities, or notes with automatic inventory synchronization
                  </p>
                </div>
              </div>
              <button
                disabled={savingEdit}
                onClick={() => setEditingRefund(null)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Edit Form */}
            <form onSubmit={handleSaveEdit} className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Row 1: Date & Reference */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Refund Date *
                  </label>
                  <input
                    type="date"
                    value={editForm.refund_date}
                    onChange={(e) => setEditForm({ ...editForm, refund_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Reference / Invoice
                  </label>
                  <div className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-600 font-mono">
                    {activeTab === 'customer'
                      ? (editingRefund.sale_invoice_number || (editingRefund.sale_id ? `INV-${editingRefund.sale_id}` : 'Direct'))
                      : (editingRefund.purchase_reference || (editingRefund.purchase_id ? `PUR-${editingRefund.purchase_id}` : 'Direct'))}
                  </div>
                </div>
              </div>

              {/* Items List for Return */}
              {editingRefund.refund_type === 'return' && editForm.items.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                    <PackageCheck className="h-4 w-4 text-emerald-600" />
                    Returned Items & Quantities
                  </h4>

                  <div className="space-y-2">
                    {editForm.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white shadow-sm">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {item.medicine_name}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            Unit: <span className="font-semibold">{item.unit_name || item.medicine_unit || 'pcs'}</span>
                            {item.conversion_factor > 1 && ` (${item.conversion_factor} pcs/unit)`}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="w-24">
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
                              Return Qty
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={item.unit_quantity}
                              onChange={(e) => handleEditItemQtyChange(idx, e.target.value)}
                              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-900 focus:border-amber-500 focus:outline-none"
                              required
                            />
                          </div>

                          <div className="w-28">
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
                              Line Refund (Rs.)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.refund_amount}
                              onChange={(e) => handleEditItemAmountChange(idx, e.target.value)}
                              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs font-bold text-red-600 focus:border-amber-500 focus:outline-none"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total Refund Amount */}
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                  Total Refund Amount (Rs.) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.refund_amount}
                  onChange={(e) => setEditForm({ ...editForm, refund_amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-bold text-red-600 focus:border-amber-500 focus:outline-none"
                  required
                />
              </div>

              {/* Reason & Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Reason
                  </label>
                  <input
                    value={editForm.reason}
                    onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                    placeholder="e.g. Expired, customer return, billing error..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Notes
                  </label>
                  <input
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 mt-2">
                <button
                  type="button"
                  disabled={savingEdit}
                  onClick={() => setEditingRefund(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 transition-all shadow-sm disabled:opacity-60"
                >
                  {savingEdit ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Saving Changes...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" /> Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* New Refund Modal Dialog */}
      {showForm && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => !submitting && setShowForm(false)} />
          <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className={clsx(
                  'flex h-10 w-10 items-center justify-center rounded-xl',
                  activeTab === 'customer' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                )}>
                  <Receipt className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {activeTab === 'customer' ? 'New Customer Refund' : 'New Supplier Refund'}
                  </h3>
                  <p className="text-xs text-gray-500">Select invoice and line items to create a consolidated return transaction</p>
                </div>
              </div>
              <button
                disabled={submitting}
                onClick={() => setShowForm(false)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Row 1: Entity & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    {activeTab === 'customer' ? 'Customer *' : 'Supplier *'}
                  </label>
                  <SearchableSelect
                    value={form.entity_id}
                    onChange={(val) => setForm({ ...form, entity_id: val, related_id: '' })}
                    options={activeTab === 'customer' ? customerOptions : supplierOptions}
                    placeholder={activeTab === 'customer' ? '-- Select Customer (or Walk-in) --' : '-- Select Supplier (or General) --'}
                    searchPlaceholder="Search name or phone..."
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Refund Date
                  </label>
                  <input
                    type="date"
                    value={form.refund_date}
                    onChange={(e) => setForm({ ...form, refund_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    required
                  />
                </div>
              </div>

              {/* Row 2: Select Invoice */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    {activeTab === 'customer' ? 'Select Sales Invoice' : 'Select Purchase Invoice'}
                  </label>
                  {loadingInvoices && (
                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                      <RefreshCw className="h-3 w-3 animate-spin" /> Loading invoices...
                    </span>
                  )}
                </div>
                <SearchableSelect
                  value={form.related_id}
                  onChange={handleInvoiceChange}
                  options={invoiceOptions}
                  placeholder={
                    loadingInvoices
                      ? 'Loading invoices...'
                      : entityInvoices.length === 0
                      ? 'No invoices found'
                      : '-- Select an Invoice (or choose Direct/Manual) --'
                  }
                  searchPlaceholder="Search invoice # or contact..."
                />
              </div>

              {/* Row 3: Refund Type Buttons */}
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                  Refund Type
                </label>
                <div className="flex gap-2">
                  {[
                    { id: 'return', label: 'Goods Return (Restores Stock)' },
                    { id: 'overpayment', label: 'Overpayment / Balance Refund' },
                  ].map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setForm({ ...form, refund_type: t.id })}
                      className={clsx(
                        'flex-1 py-2 px-3 text-xs font-semibold rounded-lg border transition-all text-center',
                        form.refund_type === t.id
                          ? activeTab === 'customer'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-500 shadow-sm'
                            : 'bg-amber-50 text-amber-700 border-amber-500 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Goods Return Mode */}
              {form.refund_type === 'return' && (
                <>
                  {/* Case A: An invoice is selected -> Show invoice line items list */}
                  {form.related_id ? (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <PackageCheck className="h-4 w-4 text-emerald-600" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                            Invoice Items to Return
                          </h4>
                        </div>
                        {invoiceItems.length > 0 && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleSelectAllItems(true)}
                              className="text-[11px] font-medium text-emerald-600 hover:underline"
                            >
                              Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => handleSelectAllItems(false)}
                              className="text-[11px] font-medium text-gray-500 hover:underline"
                            >
                              Deselect All
                            </button>
                          </div>
                        )}
                      </div>

                      {loadingInvoiceDetails ? (
                        <div className="py-8 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin" /> Loading invoice items...
                        </div>
                      ) : invoiceItems.length === 0 ? (
                        <div className="py-6 text-center text-xs text-gray-400">
                          No line items found in this invoice.
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                          {invoiceItems.map((item, idx) => (
                            <div
                              key={idx}
                              className={clsx(
                                'flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors bg-white',
                                item.selected ? 'border-emerald-300 shadow-sm' : 'border-gray-200 opacity-60'
                              )}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={() => handleToggleItem(idx)}
                                  className="text-gray-400 hover:text-emerald-600 transition-colors"
                                >
                                  {item.selected ? (
                                    <CheckSquare className="h-5 w-5 text-emerald-600" />
                                  ) : (
                                    <Square className="h-5 w-5 text-gray-400" />
                                  )}
                                </button>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {item.medicine_name}
                                  </p>
                                  <p className="text-[11px] text-gray-500">
                                    Invoiced: <span className="font-semibold">{item.invoiced_qty} {item.invoiced_unit_name || item.unit}</span> @ Rs. {item.unit_price.toFixed(2)}
                                    <span className="ml-2 text-emerald-600">Remaining: {item.max_return_qty}</span>
                                    {item.batch_number && item.batch_number !== '-' && (
                                      <span className="ml-2 font-mono text-gray-400">Batch: {item.batch_number}</span>
                                    )}
                                  </p>
                                </div>
                              </div>

                              {item.selected && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="w-32">
                                    <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
                                      Unit
                                    </label>
                                    <select
                                      value={item.selected_unit}
                                      onChange={(e) => handleItemUnitChange(idx, e.target.value)}
                                      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-900 focus:border-emerald-500 focus:outline-none"
                                    >
                                      {item.available_units.filter((u) => u.conversion_factor <= item.remaining_base_qty).map((u) => (
                                        <option key={u.unit_name} value={u.unit_name}>
                                          {u.unit_name} {u.conversion_factor > 1 ? `(${u.conversion_factor}x)` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="w-20">
                                    <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
                                      Return Qty
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.return_qty}
                                      onChange={(e) => handleItemQtyChange(idx, e.target.value)}
                                      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-900 focus:border-emerald-500 focus:outline-none"
                                      required
                                    />
                                  </div>

                                  <div className="w-24 text-right">
                                    <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
                                      Refund
                                    </span>
                                    <span className="text-sm font-bold text-red-600">
                                      Rs. {item.refund_amount}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {invoiceItems.length > 0 && (
                        <div className="flex items-center justify-between border-t border-gray-200/80 pt-3 px-1">
                          <span className="text-xs font-bold text-gray-700">
                            Total Refund for Selected Items:
                          </span>
                          <span className="text-base font-bold text-red-600">
                            Rs. {calculateTotalInvoiceRefund().toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Case B: No invoice selected -> Manual Medicine & Quantity picker */
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700">
                          <Info className="h-4 w-4 text-blue-500" />
                          Manual Medicine Return (Direct)
                        </div>
                        <button
                          type="button"
                          onClick={handleAddManualItem}
                          className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add Another Medicine
                        </button>
                      </div>

                      <div className="space-y-3">
                        {manualItems.map((mItem, mIdx) => (
                          <div key={mIdx} className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-bold text-gray-500 uppercase">Item #{mIdx + 1}</span>
                              {manualItems.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveManualItem(mIdx)}
                                  className="text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div>
                                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                  Medicine *
                                </label>
                                <SearchableSelect
                                  value={mItem.medicine_id}
                                  onChange={(val) => handleManualItemChange(mIdx, 'medicine_id', val)}
                                  options={medicineOptions}
                                  placeholder="-- Select Medicine --"
                                  searchPlaceholder="Search medicine name, generic, barcode..."
                                  onSearch={handleSearchMedicines}
                                  required
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                  Unit *
                                </label>
                                {(() => {
                                  const curMed = medicines.find((m) => String(m.id) === String(mItem.medicine_id));
                                  const units = curMed ? getMedicineUnits(curMed.id) : [];
                                  return (
                                    <select
                                      value={mItem.unit_name || ''}
                                      onChange={(e) => handleManualItemChange(mIdx, 'unit_name', e.target.value)}
                                      disabled={!mItem.medicine_id}
                                      className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-xs font-medium text-gray-900 focus:border-emerald-500 focus:outline-none disabled:bg-gray-100"
                                      required
                                    >
                                      {units.length === 0 ? (
                                        <option value="">Select medicine first</option>
                                      ) : (
                                        units.map((u) => (
                                          <option key={u.unit_name} value={u.unit_name}>
                                            {u.unit_name} {u.conversion_factor > 1 ? `(${u.conversion_factor}x)` : ''} — Rs. {(activeTab === 'customer' ? u.selling_price : u.cost_price).toFixed(2)}
                                          </option>
                                        ))
                                      )}
                                    </select>
                                  );
                                })()}
                              </div>
                              <div>
                                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                  Return Quantity *
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  value={mItem.return_quantity}
                                  onChange={(e) => handleManualItemChange(mIdx, 'return_quantity', e.target.value)}
                                  placeholder="e.g. 2"
                                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                                  required
                                />
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-600">
                                Refund Amount (Rs.) (Optional, auto-calculates if empty)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={mItem.refund_amount}
                                onChange={(e) => handleManualItemChange(mIdx, 'refund_amount', e.target.value)}
                                placeholder="Leave empty to auto-calculate from unit price"
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {manualItems.length > 0 && (
                        <div className="flex items-center justify-between border-t border-gray-200/80 pt-2 px-1">
                          <span className="text-xs font-bold text-gray-700">Total Manual Refund:</span>
                          <span className="text-sm font-bold text-red-600">
                            Rs. {manualItems.reduce((sum, item) => sum + (parseFloat(item.refund_amount) || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Overpayment Mode */}
              {form.refund_type === 'overpayment' && (
                <div className="rounded-xl border border-gray-200 bg-purple-50/40 p-4 space-y-3">
                  {selectedInvoiceData && (
                    <div className="text-xs text-purple-900 bg-purple-100/60 p-2.5 rounded-lg">
                      <span className="font-semibold">Invoice Total:</span> Rs. {(parseFloat(selectedInvoiceData.total) || 0).toFixed(2)} |{' '}
                      <span className="font-semibold">Paid Amount:</span> Rs. {(parseFloat(selectedInvoiceData.paid_amount) || 0).toFixed(2)}
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                      Refund Amount (Rs.) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.refund_amount}
                      onChange={(e) => setForm({ ...form, refund_amount: e.target.value })}
                      placeholder="0.00"
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-purple-500 focus:outline-none"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Reason & Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Reason
                  </label>
                  <input
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="e.g. Expired, damaged, excess billing..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-600">
                    Notes
                  </label>
                  <input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional details..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 mt-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setShowForm(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={clsx(
                    'flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white transition-all shadow-sm',
                    submitting ? 'opacity-70 cursor-not-allowed' : '',
                    activeTab === 'customer'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  )}
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4" /> Process Refund
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
